import React, { useState, useEffect, useCallback } from 'react';
import Modal from './Modal';
import { fetchSkiAreas } from '../services/skiAreasService';
import { useError } from '../contexts/ErrorContext';
import './SkiAreaSelectModal.css';

const SkiAreaSelectModal = ({ isOpen, onClose, onSelect }) => {
  const [skiAreas, setSkiAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { addError } = useError();

  const loadSkiAreas = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchSkiAreas();
      // Sort by name for easier browsing
      const sorted = data.sort((a, b) => a.name.localeCompare(b.name));
      setSkiAreas(sorted);
    } catch (err) {
      console.error('Failed to load ski areas:', err);
      const errorMsg = err.message || 'Failed to load ski areas';
      setError(errorMsg);
      addError(errorMsg, err.stack, 'Ski Area Select Modal');
    } finally {
      setLoading(false);
    }
  }, [addError]);

  useEffect(() => {
    if (isOpen) {
      loadSkiAreas();
      setSearchQuery('');
    }
  }, [isOpen, loadSkiAreas]);

  const handleSkiAreaSelect = (skiArea) => {
    onSelect(skiArea);
  };

  const handleUnlistedSelect = () => {
    onSelect(null); // null means unlisted
  };

  // Filter ski areas based on search query (some fields may be null/undefined)
  const normalizedQuery = (searchQuery || '').toLowerCase().trim();
  const filteredSkiAreas = skiAreas.filter((area) => {
    if (!normalizedQuery) return true;
    const name = (area?.name || '').toLowerCase();
    const country = (area?.country || '').toLowerCase();
    const region = (area?.region || '').toLowerCase();
    return (
      name.includes(normalizedQuery) ||
      country.includes(normalizedQuery) ||
      region.includes(normalizedQuery)
    );
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Select Ski Area" className="ski-area-select-modal">
      <div className="ski-area-select-content">
        {loading && <div className="ski-area-loading">Loading ski areas...</div>}
        {error && <div className="ski-area-error">Error: {error}</div>}
        {!loading && !error && (
          <>
            <div className="ski-area-search">
              <input
                type="text"
                className="ski-area-search-input"
                placeholder="Search by name, country, or region..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>
            <div className="ski-area-list">
              {/* Unlisted option at the top */}
              <div
                className="ski-area-item unlisted-item"
                onClick={handleUnlistedSelect}
              >
                <div className="ski-area-info">
                  <h3>Unlisted</h3>
                  <p className="ski-area-meta">No specific ski area</p>
                </div>
                <div className="ski-area-select-arrow">→</div>
              </div>
              
              {filteredSkiAreas.length === 0 ? (
                <p className="empty-ski-areas">No ski areas match your search.</p>
              ) : (
                filteredSkiAreas.map((area) => (
                  <div
                    key={area.id}
                    className="ski-area-item"
                    onClick={() => handleSkiAreaSelect(area)}
                  >
                    <div className="ski-area-info">
                      <h3>{area.name}</h3>
                      <p className="ski-area-meta">
                        {area.region}, {area.country}
                        {area.navigationCapable && ' • Navigation Available'}
                      </p>
                    </div>
                    <div className="ski-area-select-arrow">→</div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default SkiAreaSelectModal;

